import type { UploadProps } from 'antd'
import { Spin, Upload } from 'antd'
import { Icon } from '@iconify/react'
import { useNotification } from 'app/providers'

import styles from './patients.module.scss'
import { useParams } from 'react-router'
import { Modal } from 'common/components/Modal'
import { useToggle } from 'react-use'
import { Button } from 'common/components/Button/Button'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { useImportPrescriptionsMutation } from 'features/Profile/features/Prescriptions/patient-data.api'

const { Dragger } = Upload

type Props = UploadProps & {
  title?: string
  description?: string
  onSuccess?: () => void
  onError?: () => void
}

export const UploadDistinctPatients = ({
  title = 'Click or drag file to this area',
  description = 'Upload patients file in CSV format',
  onSuccess,
  onError,
  ...props
}: Props) => {
  const { siteId } = useParams()

  const { setNotification } = useNotification()

  const [importPrescriptions, { isLoading }] = useImportPrescriptionsMutation()

  const dragProps: UploadProps = {
    ...props,
    maxCount: 1,
    multiple: false,
    customRequest: (options) => {
      importPrescriptions({ file: options.file as File, siteId })
        .unwrap()
        .then(() => {
          onSuccess?.()
          setNotification({
            title: 'Success',
            description: 'File uploaded successfully.',
            type: ALERT_CONSTANTS.SUCCESS,
          })
        })
        .catch(() => {
          onError?.()
          setNotification({
            title: 'Error',
            description: 'File upload failed.',
            type: ALERT_CONSTANTS.ERROR,
          })
        })
    },
  }

  return (
    <Spin spinning={isLoading} wrapperClassName={styles.uploadPrescriptionsContainer}>
      <Dragger {...dragProps} disabled={isLoading} showUploadList={false}>
        <p className='ant-upload-drag-icon'>
          <Icon icon='ant-design:inbox-outlined' width={60} height={60} color='var(--blue)' />
        </p>
        <p className='ant-upload-text'>{title}</p>
        <p className='ant-upload-hint'>{description}</p>
      </Dragger>
    </Spin>
  )
}

export const DialogUploadDistinctPatients = ({ ...dragProps }: Props) => {
  const [isOpen, toggleOpen] = useToggle(false)
  return (
    <>
      <Button onClick={toggleOpen}>Upload Patients</Button>
      <Modal title='Upload Patients' open={isOpen} onCancel={toggleOpen}>
        <UploadDistinctPatients {...dragProps} onSuccess={toggleOpen} />
      </Modal>
    </>
  )
}
